import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CustomError } from '@app/modules/error-handler/custom-error.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ICredentials } from 'src/app/shared/interfaces/ICredentials';
import { IMfaCodeModel } from 'src/app/shared/interfaces/IMfaCodeModel';
import { IEnvironmentConfig } from 'src/webpack/extra-webpack-types';
import { getInactivityTimeout, initInactivityTimeout } from '@orthofi/auth-core';
import type { OrthoFiEnvironment } from '@orthofi/auth-core';
import { tap } from 'rxjs/operators';

/** Credentials to be used when interacting with the service-auth API */
const httpOptions = { withCredentials: true };

// TODO: Move these interfaces to their own files.
/** The generic response service-auth will return. */
export interface IKeymasterResponse {
  /** Text information about the status of the result. */
  Message: string;
}
/** The response returned by service-auth when a login attempt is made. */
export interface ILoginResponse {
  /** A url service-auth recommends the user should be sent to after login. */
  redirectUrl: string;
  isMfaRequired: boolean;
  isMfaConfigurationRequired: boolean;
  mfaDisplayName: string;
  mfaPhoneNumberDisplay: string;
  isLegacyPasswordFlow: boolean;
  isEmailRequired: boolean;
}
/** The post information required by service-auth for password resets. */
export interface IPasswordResetPayload {
  /** The single use token that was generated and sent to the user. */
  resetToken: string;
  /** The desired new password. */
  newPassword: string;
}
/** The post information required by service-auth for identifying a user. */
export interface IIdentifyPayload {
  username: string;
}
/** The response returned by service-auth when identifying a user. */
export interface IIdentifyResponse {
  isInternalUser: boolean;
}

export interface IExchangeOpenIdTokenPayload {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

/** An API based data service for interacting with the service-auth API, including authenticating a user's credentials and reset them. */
@Injectable()
export class AuthenticationService {
  /** @ignore */
  apiRoot: string;
  serviceAuthApiRoot: string;
  localUserApiRoot = 'http://localhost:9200';
  isLocal = false;

  /** @ignore */
  constructor(
    @Inject('ENV') private _env: IEnvironmentConfig,
    private _http: HttpClient
  ) {
    this.apiRoot = `${_env.authenticationApi}/api/account`;
    this.serviceAuthApiRoot = _env.serviceAuthApi;
    this.isLocal = _env.errorLoggerOptions.environment === "Local";
  }

  private async establishSessionTimeout(): Promise<void> {
    const instance =
      getInactivityTimeout() ??
      (await initInactivityTimeout({
        environment: this._env.errorLoggerOptions.environment as OrthoFiEnvironment,
        redirect: () => {
          window.location.replace(this._env.errorHandlerOptions.authRedirectUrl);
        },
      }));
    instance.resetTimeout();
  }

  /**
   * Log in to the system and have the session cookie attached.
   * @param login An object containing credentials.
   */
  authenticate(login: ICredentials): Observable<ILoginResponse> {
    if (this.isLocal) {
      return this._http.post<any>(`${this.localUserApiRoot}/token/local`, { UserName: login.Username }, httpOptions).pipe(
        switchMap((response) => this.exchangeOpenIdToken({ accessToken: response.access_token, idToken: "empty", refreshToken: "empty" })),
        map((response) => ({
          ...response,
          redirectUrl: this._env.portalUrl.replace(/^http?\:\/\//i, "").replace(/^https?\:\/\//i, ""),
          isMfaRequired: false,
          isMfaConfigurationRequired: false,
          isEmailRequired: false,
        })),
        catchError((loginError: any) => {
          throw loginError;
        })
      )
    }
    return this._http.post<any>(`${this.apiRoot}/login`, login, httpOptions).pipe(
      tap(() => void this.establishSessionTimeout()),
      catchError((loginError: any) => {
        throw loginError;
      })
    );
  }

  identify(identification: IIdentifyPayload): Observable<IIdentifyResponse> {
    return this._http.post<any>(`${this.apiRoot}/identify`, identification, httpOptions).pipe(
      catchError((identifyError: any) => {
        throw identifyError;
      })
    );
  }

  exchangeOpenIdToken(payload: IExchangeOpenIdTokenPayload): Observable<ILoginResponse> {
    return this._http.post<any>(`${this.serviceAuthApiRoot}/api/account/exchange-openid-token`, payload, httpOptions).pipe(
      tap(() => void this.establishSessionTimeout()),
      catchError((exchangeError: any) => {
        throw exchangeError;
      })
    );
  }

  // TODO: remove once use-service-auth-exchange flag is fully rolled out
  exchangeOpenIdTokenKeymaster(payload: IExchangeOpenIdTokenPayload): Observable<ILoginResponse> {
    return this._http.post<any>(`${this.apiRoot}/exchange-openid-token`, payload, httpOptions).pipe(
      tap(() => void this.establishSessionTimeout()),
      catchError((exchangeError: any) => {
        throw exchangeError;
      })
    );
  }

  refreshOpenIdToken(): Observable<void> {
    return this._http.post<any>(`${this.serviceAuthApiRoot}/api/account/refresh-openid-token`, {}, httpOptions);
  }

  verifyCode(model: IMfaCodeModel): Observable<ILoginResponse> {
    return this._http.post<any>(`${this.apiRoot}/verify-mfa`, model, httpOptions).pipe(
      catchError((loginError: any) => {
        throw loginError;
      })
    );
  }

  /**
   * Log out and have the session cookie deleted. Will not redirect.
   */
  logout(): Observable<ILoginResponse> {
    return this._http.post<any>(`${this.serviceAuthApiRoot}/api/account/logout`, {}, httpOptions).pipe(
      catchError((loginError: any) => {
        return throwError(
          new CustomError(`Sorry, we couldn't clear session data. Please try again.`, {
            level: 'info',
            notificationPriority: 'minor',
            showSupportId: false,
            errorObject: loginError,
          })
        );
      })
    );
  }

  /**
   * Using a previously generated `resetToken`, change the associated account's password to a `newPassword`.
   * @param reset An object containing the `resetToken` and `newPassword`.
   */
  resetPassword(reset: IPasswordResetPayload): Observable<IKeymasterResponse> {
    return this._http.post<any>(`${this.serviceAuthApiRoot}/api/account/resetpassword`, reset, httpOptions).pipe(
      catchError((resetError: any) => {
        //return throwError(new CustomError(
        //  `Sorry, we couldn't reset the password at this time. Please try again.`,
        //  { showSupportId: true, errorObject: resetError }
        //));
        throw resetError;
      })
    );
  }

  /**
   * Make a request to check if the username exists and have an email containing the reset token sent.
   * @param username The username of the account to check for and contact.
   */
  sendForgotPasswordEmail(username: string): Observable<IKeymasterResponse> {
    return this._http
      .post<IKeymasterResponse>(`${this.serviceAuthApiRoot}/api/account/resetpasswordemail`, { username }, httpOptions)
      .pipe(
        catchError((sendEmailError: any) => {
          // eslint-disable-next-line max-len
          const defaultMessage =
            "Sorry, we either encountered an error when attempting to send the email or an account with that username doesn't exist. If an email doesn't arrive, please refresh this page and try again or contact your practice for assistance.";
          const { error = {} } = sendEmailError;
          const { data = [], error_message: serverMessage } = error;
          const displayMessage = data[0]?.displayMessage || false;
          const customMessage = displayMessage ? serverMessage : defaultMessage;
          const noUser = customMessage.indexOf('No user found') < 1;

          // TODO: Should this message reveal that the email address isn't registered?
          return throwError(
            new CustomError(customMessage, {
              showSupportId: !noUser,
              level: noUser ? 'info' : 'error',
              errorObject: sendEmailError,
            })
          );
        })
      );
  }
}
